import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { Label } from '@/components/ui/Label';
import { useUiStore } from '@/stores/ui';

export type LocaleKey = 'en' | 'ar' | 'eg-ar';

export interface LocaleValue {
  en: string;
  ar?: string;
  'eg-ar'?: string;
}

interface LocaleFieldProps {
  /** Stable id used to compose input ids. */
  id: string;
  label: React.ReactNode;
  hint?: React.ReactNode;
  value: LocaleValue;
  onChange: (next: LocaleValue) => void;
  /** Locales to expose as tabs. Default: en, ar (eg-ar opt-in). */
  locales?: readonly LocaleKey[];
  /**
   * Controlled active locale. When set, the host owns the tab state — used by
   * screens that show per-locale context (baseline, diff, status) next to the
   * field and must stay in step with it. Uncontrolled by default.
   */
  activeLocale?: LocaleKey;
  onActiveLocaleChange?: (locale: LocaleKey) => void;
  /**
   * Render the built-in tab strip. Turn it off when the host already renders a
   * richer locale switcher (status chips, per-locale actions) driving
   * `activeLocale` — two identical strips would be redundant, not clearer.
   */
  renderTabs?: boolean;
  required?: readonly LocaleKey[];
  errors?: Partial<Record<LocaleKey, string>>;
  multiline?: boolean;
  placeholder?: Partial<Record<LocaleKey, string>>;
  rows?: number;
  maxLength?: number;
}

const LOCALE_LABEL: Record<LocaleKey, string> = {
  en: 'EN',
  ar: 'AR',
  'eg-ar': 'EG-AR',
};
const LOCALE_DIR: Record<LocaleKey, 'ltr' | 'rtl'> = {
  en: 'ltr',
  ar: 'rtl',
  'eg-ar': 'rtl',
};

export function LocaleField({
  id,
  label,
  hint,
  value,
  onChange,
  locales = ['en', 'ar'],
  activeLocale,
  onActiveLocaleChange,
  renderTabs = true,
  required = ['en'],
  errors,
  multiline,
  placeholder,
  rows = 4,
  maxLength,
}: LocaleFieldProps) {
  const { t } = useTranslation('common');
  const workingLocale = useUiStore((s) => s.locale);
  // Default the active tab to the working locale (admin's Settings preference)
  // when that locale is exposed by this field. Otherwise fall back to the first
  // available locale. F-AD-16: keeps the editing surface aligned with the
  // operator's chosen voice.
  const initialActive: LocaleKey = locales.includes(workingLocale)
    ? workingLocale
    : (locales[0] ?? 'en');
  const [uncontrolledActive, setUncontrolledActive] = React.useState<LocaleKey>(initialActive);
  const active = activeLocale ?? uncontrolledActive;

  function selectLocale(locale: LocaleKey) {
    if (activeLocale === undefined) setUncontrolledActive(locale);
    onActiveLocaleChange?.(locale);
  }

  function update(locale: LocaleKey, v: string) {
    onChange({ ...value, [locale]: v });
  }

  const inputId = `${id}-${active}`;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const activeError = errors?.[active];
  const activeRequired = required.includes(active);
  const placeholderForActive = placeholder?.[active];

  return (
    <div className="mb-4" role="group" aria-labelledby={`${id}-label`}>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <Label id={`${id}-label`} htmlFor={inputId} className="mb-0">
          {label}
          {activeRequired ? <span className="ms-1 text-[color:var(--color-danger)]" aria-hidden>*</span> : null}
        </Label>
        {/* Omitted entirely, not merely hidden: a second tablist with the
            same label would still be announced to a screen reader. */}
        {renderTabs ? (
        <div
          className="flex gap-1"
          role="tablist"
          aria-label={t('localeField.tablist', { defaultValue: 'Locales' })}
        >
          {locales.map((loc) => {
            const filled = Boolean(value[loc]?.length);
            const selected = active === loc;
            return (
              <button
                key={loc}
                role="tab"
                type="button"
                aria-selected={selected}
                aria-controls={`${id}-panel-${loc}`}
                onClick={() => selectLocale(loc)}
                className={cn(
                  'rounded-[var(--radius-pill)] px-2.5 py-1 text-xs font-semibold uppercase tracking-wide',
                  selected
                    ? 'bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]'
                    : 'bg-[color:var(--color-surface-2)] text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-surface-3)]',
                  !selected && filled && 'ring-1 ring-[color:var(--color-success)]'
                )}
              >
                {LOCALE_LABEL[loc]}
              </button>
            );
          })}
        </div>
        ) : null}
      </div>
      <div
        id={`${id}-panel-${active}`}
        {...(renderTabs ? { role: 'tabpanel', 'aria-labelledby': `${id}-label` } : {})}
      >
        {multiline ? (
          <textarea
            id={inputId}
            dir={LOCALE_DIR[active]}
            value={value[active] ?? ''}
            onChange={(e) => update(active, e.target.value)}
            rows={rows}
            maxLength={maxLength}
            placeholder={placeholderForActive}
            aria-invalid={Boolean(activeError)}
            aria-describedby={
              [hint ? hintId : null, activeError ? errorId : null].filter(Boolean).join(' ') ||
              undefined
            }
            className={cn(
              'block w-full rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-3 text-sm text-[color:var(--color-fg)] shadow-sm',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)] focus-visible:ring-offset-2',
              'aria-[invalid=true]:border-[color:var(--color-danger)]'
            )}
          />
        ) : (
          <input
            id={inputId}
            dir={LOCALE_DIR[active]}
            value={value[active] ?? ''}
            onChange={(e) => update(active, e.target.value)}
            maxLength={maxLength}
            placeholder={placeholderForActive}
            aria-invalid={Boolean(activeError)}
            aria-describedby={
              [hint ? hintId : null, activeError ? errorId : null].filter(Boolean).join(' ') ||
              undefined
            }
            className={cn(
              'block h-11 w-full rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 text-sm text-[color:var(--color-fg)] shadow-sm',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)] focus-visible:ring-offset-2',
              'aria-[invalid=true]:border-[color:var(--color-danger)]'
            )}
          />
        )}
      </div>
      {hint ? (
        <p id={hintId} className="mt-1 text-xs text-[color:var(--color-fg-muted)]">
          {hint}
        </p>
      ) : null}
      {activeError ? (
        <p id={errorId} role="alert" className="mt-1 text-xs font-medium text-[color:var(--color-danger)]">
          {activeError}
        </p>
      ) : null}
    </div>
  );
}
